import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MapContainer, TileLayer, Marker, useMapEvents, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useTranslation } from "react-i18next";

interface LocationMapPickerProps {
  latitude: number | null | undefined;
  longitude: number | null | undefined;
  onLocationSelect: (lat: number, lng: number) => void;
}

// Component to handle map events
function MapEvents({ onLocationSelect }: { onLocationSelect: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

export function LocationMapPicker({ latitude, longitude, onLocationSelect }: LocationMapPickerProps) {
  const { t } = useTranslation();
  const [position, setPosition] = useState<[number, number]>(
    latitude && longitude ? [latitude, longitude] : [41.0082, 28.9784] // Default to Istanbul if no coordinates
  );
  const mapRef = useRef<L.Map>(null);

  // Fix Leaflet icon issue
  useEffect(() => {
    // Fix Leaflet default icon issue
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
      iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
      shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
    });
  }, []);

  // Update position when props change
  useEffect(() => {
    if (latitude && longitude && mapRef.current) {
      setPosition([latitude, longitude]);
      mapRef.current.setView([latitude, longitude], mapRef.current.getZoom());
    }
  }, [latitude, longitude]);

  const handleCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setPosition([latitude, longitude]);
          onLocationSelect(latitude, longitude);
          if (mapRef.current) {
            mapRef.current.setView([latitude, longitude], mapRef.current.getZoom());
          }
        },
        (error) => {
          console.error("Error getting current location:", error);
        }
      );
    }
  };

  const handleMarkerDragEnd = (e: L.LeafletEvent) => {
    const marker = e.target;
    const newPosition = marker.getLatLng();
    setPosition([newPosition.lat, newPosition.lng]);
    onLocationSelect(newPosition.lat, newPosition.lng);
  };


  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{t("location.clickToSetLocation")}</p>
        <Button type="button" variant="outline" size="sm" onClick={handleCurrentLocation}>
          {t("location.useCurrentLocation")}
        </Button>
      </div>

      <Card className="h-[300px] w-full overflow-hidden">
        <div className="h-full w-full">
          <MapContainer center={position} zoom={13} style={{ height: "100%", width: "100%" }} ref={mapRef}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapEvents onLocationSelect={onLocationSelect} />
            {latitude && longitude && (
              <Marker
                position={[latitude, longitude]}
                draggable={true}
                eventHandlers={{
                  dragend: handleMarkerDragEnd,
                }}
              >
                <Popup>{t("location.selectedLocation")}</Popup>
              </Marker>
            )}
          </MapContainer>
        </div>
      </Card>

      {latitude && longitude && (
        <p className="text-sm text-muted-foreground text-center">
          {t("location.selectedLocation", { latitude: latitude.toFixed(6), longitude: longitude.toFixed(6) })}
        </p>
      )}
    </div>
  );
}
